import { useState } from "react";

import { ApiError } from "@wellcheck/api-client";
import { Button } from "@wellcheck/components/ui/button";

import { CampaignAbortedNotice } from "./CampaignAbortedNotice";
import { Step1RespondentsUpload } from "./Step1RespondentsUpload";
import { Step2Confirm } from "./Step2Confirm";
import { Step3PeriodInfo } from "./Step3PeriodInfo";

import type { PeriodInfoInput } from "@/features/upload/data/periodInfoSchema";
import type { ParseError, Respondent } from "@/features/upload/data/respondentsCsv";

import { WizardProgress } from "@/components/WizardProgress";
import { isCampaignAbortedError } from "@/features/upload/api/campaignAborted";
import {
  UploadConflictError,
  UploadValidationError,
  useSubmitUpload,
} from "@/features/upload/api/useSubmitUpload";

const STEPS = [
  { label: "実施情報" },
  { label: "名簿アップロード" },
  { label: "内容確認" },
] as const;

type StepIndex = 0 | 1 | 2;

const PERIOD_INFO_FORM_ID = "wizard-period-info";

function toSubmitErrorMessage(error: unknown): string | undefined {
  if (error instanceof UploadValidationError) return undefined;
  if (error instanceof UploadConflictError) {
    return "すでに弊社側で名簿が確定されています。修正が必要な場合は担当者へご連絡ください。";
  }
  if (error instanceof ApiError && error.status === 401) {
    return "このリンクは有効期限切れか、すでに無効になっています。担当者へご連絡ください。";
  }
  return "送信に失敗しました。時間をおいて再度お試しください。解消しない場合は担当者へご連絡ください。";
}

/**
 * Wizard — 顧客アップロードの 3 ステップ進行を司る最上位コンポーネント。
 *
 * 構成は実施情報 → 名簿アップロード → 送信。
 *   - 実施情報を先に入れることで何をアップロードするのか目的が明確になる
 *   - 名簿アップロードでは CSV 選択直後にクライアント側 preview を行うため
 *     「ファイル選択」と「内容確認」を 1 ステップに統合
 *   - パース成功で同 step 内の「次へ」が enable、押下で送信確認へ
 *
 * 各 step は順次実装。Step 0 (実施情報) は本コミットで実装済み、
 * Step 1 / 2 は後続タスクで実装する。状態は memory のみ。
 */
export function Wizard() {
  const [stepIndex, setStepIndex] = useState<StepIndex>(0);
  const [periodInfo, setPeriodInfo] = useState<PeriodInfoInput | undefined>(undefined);
  const [respondents, setRespondents] = useState<Respondent[]>([]);
  const [csvText, setCsvText] = useState<string>("");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [submitErrors, setSubmitErrors] = useState<ParseError[]>([]);
  const submitUpload = useSubmitUpload();

  const goNext = () => {
    setStepIndex((i) => (i < 2 ? ((i + 1) as StepIndex) : i));
  };
  const goBack = () => {
    setStepIndex((i) => (i > 0 ? ((i - 1) as StepIndex) : i));
  };

  const handlePeriodInfoSubmit = (values: PeriodInfoInput) => {
    setPeriodInfo(values);
    goNext();
  };

  const handleSubmit = () => {
    if (!periodInfo) return;
    setSubmitErrors([]);
    submitUpload.mutate(
      {
        csvText,
        periodStart: periodInfo.periodStart,
        periodEnd: periodInfo.periodEnd,
        notes: periodInfo.notes,
      },
      {
        onError: (error) => {
          if (error instanceof UploadValidationError) {
            setSubmitErrors(error.errors);
            // 行単位エラーは Step1 (名簿アップロード) で表示するため、確認 dialog は閉じて
            // Step1 に戻す (フォーム入力自体はやり直させない)。
            setStepIndex(1);
          }
        },
      },
    );
  };

  const submitErrorMessage = submitUpload.isError
    ? toSubmitErrorMessage(submitUpload.error)
    : undefined;

  if (submitUpload.isSuccess) {
    return <SubmittedView />;
  }

  // submit 時点で案件がキャンセルされていた場合、UploadPage の未訪問リンク時と同じ
  // 案内画面に切り替える (ADR-0055 Decision 3)。token は消費済みでも再送導線はない。
  if (submitUpload.isError && isCampaignAbortedError(submitUpload.error)) {
    return <CampaignAbortedNotice />;
  }

  return (
    <div className="space-y-10">
      <WizardProgress steps={STEPS} currentIndex={stepIndex} />

      {stepIndex === 0 && (
        <StepBlock title="実施情報" description="ストレスチェック実施に必要な情報を入力します。">
          <Step3PeriodInfo
            defaultValues={periodInfo}
            onValidSubmit={handlePeriodInfoSubmit}
            formId={PERIOD_INFO_FORM_ID}
          />
        </StepBlock>
      )}
      {stepIndex === 1 && (
        <StepBlock
          title="名簿アップロード"
          description="名簿 CSV を選ぶとブラウザ内で内容を確認します。問題がなければ次へ進めます。"
        >
          <Step1RespondentsUpload
            defaultValue={respondents}
            onParsed={setRespondents}
            onFileText={setCsvText}
            submitErrors={submitErrors}
          />
        </StepBlock>
      )}
      {stepIndex === 2 && periodInfo && (
        <StepBlock
          title="内容確認"
          description="送信前に内容をご確認ください。送信後は編集できません。"
        >
          <Step2Confirm
            periodInfo={periodInfo}
            respondents={respondents}
            dialogOpen={confirmOpen}
            onDialogOpenChange={setConfirmOpen}
            onConfirm={handleSubmit}
            submitError={submitErrorMessage}
          />
        </StepBlock>
      )}

      <div className="flex items-center justify-end gap-6 border-t border-border pt-6">
        {stepIndex > 0 && (
          <Button variant="link" onClick={goBack} className="px-0 text-muted-foreground">
            戻る
          </Button>
        )}
        {stepIndex === 0 ? (
          // Step 0 は zod バリデーション経由で次へ進めたいので form の submit に委譲
          <Button size="lg" type="submit" form={PERIOD_INFO_FORM_ID}>
            次へ
          </Button>
        ) : stepIndex === 1 ? (
          <Button size="lg" onClick={goNext} disabled={respondents.length === 0}>
            次へ
          </Button>
        ) : (
          <Button size="lg" onClick={() => setConfirmOpen(true)} disabled={submitUpload.isPending}>
            確定して送信
          </Button>
        )}
      </div>
    </div>
  );
}

interface StepBlockProps {
  title: string;
  description: string;
  children: React.ReactNode;
}

function StepBlock({ title, description, children }: StepBlockProps) {
  return (
    <section className="space-y-4">
      <header className="space-y-1">
        <h2 className="text-base font-semibold">{title}</h2>
        <p className="text-sm text-muted-foreground">{description}</p>
      </header>
      <div>{children}</div>
    </section>
  );
}

/**
 * SubmittedView — 送信完了後の画面。
 *
 * 不可逆である旨は確認ダイアログ側で既に伝えているので、ここでは結果通知と
 * 次のアクション (連絡導線) のみシンプルに提示する。Wizard 進捗バーは隠す。
 */
function SubmittedView() {
  return (
    <section className="space-y-4 rounded-md border border-border bg-card px-6 py-8 text-center">
      <h2 className="text-lg font-semibold">送信が完了しました</h2>
      <p className="text-sm text-muted-foreground">
        ご記入いただきありがとうございました。
        <br />
        このページは閉じていただいて構いません。修正が必要な場合は弊社担当者へご連絡ください。
      </p>
    </section>
  );
}
